// The operations overview: three numbers that decide something, one large trend
// chart, and the week-by-week money underneath.
//
// What it replaces was five KPI cards of equal weight, each with its own
// thumbnail chart. Five equal cards say nothing is more important than anything
// else, and a chart 90 pixels wide cannot show a trend - so the page carried a
// lot of ink and answered no question. The rule here is that the page answers
// three: what needs attention now (top), which way it is going (the chart), and
// what it earned (the weeks below).
//
// Money is deliberately not in the chart. Dollars and container counts do not
// share an axis, and drawing them on one - by scaling one to fit the other -
// makes a picture where the shapes are real and the comparison between them is
// invented. Money gets its own section, at full precision, by week.

import { normalizeContainerStatus } from './container-status-presentation.mjs?v=20260806.100';

const WEEK_MILLIS = 7 * 24 * 60 * 60 * 1000;
const COMPLETE = new Set(['empty_returned', 'empty_returned_to_terminal', 'completed']);
const CANCELLED = new Set(['cancelled', 'canceled']);

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function dateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value : null;
  if (typeof value.toDate === 'function') return dateValue(value.toDate());
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function statusOf(row) {
  return normalizeContainerStatus(row?.ocean_status || row?.status || row?.current_stage || '');
}

function isComplete(row) {
  return row?.canonical_completed === true
    || row?.is_empty_returned === true
    || COMPLETE.has(statusOf(row));
}

function isCancelled(row) {
  return CANCELLED.has(statusOf(row));
}

function cents(row, key) {
  const value = Number(row?.[key]);
  return Number.isFinite(value) ? Math.round(value) : 0;
}

export function weekStart(date) {
  if (!date) return null;
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  return start;
}

function weekKey(date) {
  const start = weekStart(date);
  return start
    ? `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`
    : '';
}

export function formatMoney(centsValue, locale = 'en-US') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((Number(centsValue) || 0) / 100);
}

// Every money field the container registry carries, grouped the way an
// accountant reads them rather than the way they happen to be stored. A field
// that exists on the record but is not listed here would be silently dropped
// from the totals, so the list is the contract - see the parity test.
export const MONEY_CATEGORIES = Object.freeze([
  Object.freeze({
    group: 'revenue',
    label: 'Revenue',
    fields: Object.freeze([
      ['customer_quote_cents', 'Customer Quote'],
      ['customer_receivable_cents', 'Receivable'],
      ['customer_special_fee_cents', 'Customer Special Fee'],
      ['service_fee_cents', 'Service Fee'],
    ]),
  }),
  Object.freeze({
    group: 'cost',
    label: 'Cost',
    fields: Object.freeze([
      ['pickup_cost_cents', 'Pickup'],
      ['return_cost_cents', 'Return'],
      ['transfer_cost_cents', 'Transfer'],
      ['pier_pass_cents', 'Pier Pass'],
      ['special_fee_amount_cents', 'Special Fee'],
      ['exception_fee_cents', 'Exception Fee'],
      ['total_expense_cents', 'Total Expense (recorded)'],
    ]),
  }),
]);

// total_expense_cents is a recorded roll-up of the other cost lines, so adding
// it to them would count the same dollars twice. It is shown for comparison
// against the sum and excluded from it.
const COST_ROLLUP_FIELD = 'total_expense_cents';

export function weeklyMoney({ rows = [], now = new Date(), weeks = 8, locale = 'en-US' } = {}) {
  const safeNow = dateValue(now) || new Date();
  const current = weekStart(safeNow);
  const span = Math.max(1, Math.min(52, Number(weeks) || 8));
  const buckets = Array.from({ length: span }, (_, index) => {
    const start = new Date(current.getTime() - ((span - 1 - index) * WEEK_MILLIS));
    return {
      key: weekKey(start),
      start,
      containers: 0,
      profit_cents: 0,
      revenue_cents: 0,
      cost_cents: 0,
      fields: Object.create(null),
    };
  });
  const byKey = new Map(buckets.map((bucket) => [bucket.key, bucket]));

  for (const row of Array.isArray(rows) ? rows : []) {
    if (!isComplete(row)) continue;
    const settledAt = dateValue(row?.empty_returned_at)
      || dateValue(row?.returned_at)
      || dateValue(row?.closed_at)
      || dateValue(row?.updated_at);
    const bucket = byKey.get(weekKey(settledAt));
    if (!bucket) continue;
    bucket.containers += 1;
    bucket.profit_cents += cents(row, 'profit_cents');
    for (const category of MONEY_CATEGORIES) {
      for (const [field] of category.fields) {
        const amount = cents(row, field);
        if (!amount) continue;
        bucket.fields[field] = (bucket.fields[field] ?? 0) + amount;
        if (category.group === 'revenue') bucket.revenue_cents += amount;
        else if (field !== COST_ROLLUP_FIELD) bucket.cost_cents += amount;
      }
    }
  }

  const label = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });
  return Object.freeze(buckets.map((bucket) => Object.freeze({
    ...bucket,
    fields: Object.freeze({ ...bucket.fields }),
    label: `${label.format(bucket.start)} – ${label.format(new Date(bucket.start.getTime() + (6 * 24 * 60 * 60 * 1000)))}`,
    shortLabel: label.format(bucket.start),
  })).reverse());
}

// ---------- markup ----------

const COPY = Object.freeze({
  en: Object.freeze({
    active: 'Active Containers',
    risk: 'At Risk',
    profit: 'Profit',
    activeHint: 'Released to a driver, not yet returned',
    riskHint: 'Past due and still open',
    profitHint: 'Completed containers, all time',
    trend: 'Six-week trend',
    money: 'Weekly Financials',
    moneyHint: 'Completed containers, settled by week. Open a week for every recorded amount.',
    week: 'Week',
    containers: 'Containers',
    revenue: 'Revenue',
    cost: 'Cost',
    net: 'Profit',
    noWeeks: 'No completed containers in this period.',
    noAmounts: 'No amounts recorded for this week.',
    sumOfLines: 'Sum of cost lines',
  }),
  'zh-CN': Object.freeze({
    active: '在运货柜',
    risk: '风险柜',
    profit: '利润',
    activeHint: '已派车、尚未还空',
    riskHint: '已逾期且未结束',
    profitHint: '已完成货柜，累计',
    trend: '六周趋势',
    money: '每周财务',
    moneyHint: '按结算周统计已完成货柜。展开一周可见全部记录金额。',
    week: '周',
    containers: '货柜数',
    revenue: '收入',
    cost: '成本',
    net: '利润',
    noWeeks: '此期间没有已完成的货柜。',
    noAmounts: '本周没有记录任何金额。',
    sumOfLines: '成本明细合计',
  }),
});

export function overviewCopy(locale = 'en') {
  return COPY[locale] || COPY.en;
}

function moneyWeekMarkup(week, copy, locale) {
  const lines = MONEY_CATEGORIES.map((category) => {
    const rows = category.fields
      .map(([field, label]) => [label, week.fields[field] ?? 0, field])
      .filter(([, amount]) => amount !== 0);
    if (!rows.length) return '';
    return `<section class="ops-money__group">
      <h4>${escapeHtml(category.label)}</h4>
      <dl class="ops-money__lines">
        ${rows.map(([label, amount, field]) => `<div class="ops-money__line${field === COST_ROLLUP_FIELD ? ' ops-money__line--rollup' : ''}">
          <dt>${escapeHtml(label)}</dt><dd>${escapeHtml(formatMoney(amount, locale))}</dd>
        </div>`).join('')}
        ${category.group === 'cost' ? `<div class="ops-money__line ops-money__line--sum">
          <dt>${escapeHtml(copy.sumOfLines)}</dt><dd>${escapeHtml(formatMoney(week.cost_cents, locale))}</dd>
        </div>` : ''}
      </dl>
    </section>`;
  }).join('');
  const body = lines || `<p class="ops-money__empty">${escapeHtml(copy.noAmounts)}</p>`;
  return `<details class="ops-money__week" data-ops-money-week="${escapeHtml(week.key)}">
    <summary class="ops-money__summary">
      <span class="ops-money__range">${escapeHtml(week.label)}</span>
      <span class="ops-money__stat"><i>${escapeHtml(copy.containers)}</i>${escapeHtml(String(week.containers))}</span>
      <span class="ops-money__stat"><i>${escapeHtml(copy.revenue)}</i>${escapeHtml(formatMoney(week.revenue_cents, locale))}</span>
      <span class="ops-money__stat"><i>${escapeHtml(copy.cost)}</i>${escapeHtml(formatMoney(week.cost_cents, locale))}</span>
      <span class="ops-money__stat ops-money__stat--net" data-negative="${week.profit_cents < 0 ? 'true' : 'false'}"><i>${escapeHtml(copy.net)}</i>${escapeHtml(formatMoney(week.profit_cents, locale))}</span>
    </summary>
    <div class="ops-money__detail">${body}</div>
  </details>`;
}


// The weekly money, on its own, for the Status page (Alan, 2026-08-05). It began
// life on the Dashboard beside a trend chart and three headline numbers; the
// chart and the numbers were removed and the Dashboard's original KPI cards
// restored. Money belongs where the business is read, not where the day's work
// is run.
export function weeklyFinancialsMarkup({ weeks = [], locale = 'en' } = {}) {
  const copy = overviewCopy(locale);
  const body = weeks.length
    ? weeks.map((week) => moneyWeekMarkup(week, copy, locale)).join('')
    : `<p class="ops-money__empty">${escapeHtml(copy.noWeeks)}</p>`;
  return `<section class="ops-money" data-weekly-financials aria-label="${escapeHtml(copy.money)}">
    <header class="ops-money__head">
      <h2>${escapeHtml(copy.money)}</h2>
    </header>
    <div class="ops-money__list">${body}</div>
  </section>`;
}

// ---------- the trend ----------
//
// One large multi-line chart, on the Status page above the weekly money
// (Alan, 2026-08-05). Drawn to be read rather than admired: 2px strokes, a
// desaturated palette, hairline gridlines, no fills, no glow, no large round
// dots. A chart that looks like a toy gets read like one.

export const TREND_SERIES = Object.freeze([
  Object.freeze({ key: 'total', label: 'Opened', color: '#33507a' }),
  Object.freeze({ key: 'active', label: 'Active', color: '#4a6fa5' }),
  Object.freeze({ key: 'completed', label: 'Completed', color: '#2f7d63' }),
  Object.freeze({ key: 'risk', label: 'At Risk', color: '#a8483c' }),
]);

const CHART = Object.freeze({ width: 1200, height: 420, top: 24, right: 24, bottom: 44, left: 56 });

function niceCeiling(value) {
  if (value <= 4) return 4;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  return Math.ceil(value / magnitude) * magnitude;
}

export function trendChartMarkup({ series = {}, locale = 'en' } = {}) {
  const labels = Array.isArray(series.labels) ? series.labels.map(String) : [];
  const shown = TREND_SERIES.filter((item) => Array.isArray(series[item.key]) && series[item.key].length);
  if (labels.length < 2 || !shown.length) {
    return `<p class="ops-trend__empty">${escapeHtml(locale === 'zh-CN'
      ? '暂无足够数据绘制趋势。' : 'Not enough history yet to draw a trend.')}</p>`;
  }
  const peak = niceCeiling(Math.max(1, ...shown.flatMap((item) => series[item.key].map(Number))));
  const plotWidth = CHART.width - CHART.left - CHART.right;
  const plotHeight = CHART.height - CHART.top - CHART.bottom;
  const x = (index) => CHART.left + (index / (labels.length - 1)) * plotWidth;
  const y = (value) => CHART.top + plotHeight - ((Number(value) || 0) / peak) * plotHeight;

  // Five hairlines and their values. Enough to read a level off, few enough that
  // the grid stays behind the data instead of competing with it.
  const grid = [0, 0.25, 0.5, 0.75, 1].map((fraction) => {
    const value = Math.round(peak * fraction);
    const lineY = y(value);
    return `<line class="ops-trend__grid" x1="${CHART.left}" y1="${lineY}" x2="${CHART.width - CHART.right}" y2="${lineY}"></line>
      <text class="ops-trend__axis" x="${CHART.left - 12}" y="${lineY + 4}" text-anchor="end">${value}</text>`;
  }).join('');

  const xAxis = labels.map((label, index) => (
    `<text class="ops-trend__axis" x="${x(index)}" y="${CHART.height - 16}" text-anchor="middle">${escapeHtml(label)}</text>`
  )).join('');

  const lines = shown.map((item) => {
    const points = series[item.key].slice(0, labels.length);
    const path = points.map((value, index) => `${index === 0 ? 'M' : 'L'}${x(index).toFixed(1)},${y(value).toFixed(1)}`).join(' ');
    // Small vertices, not bubbles: they mark where a reading exists without
    // turning the line into a string of beads.
    const marks = points.map((value, index) => (
      `<circle class="ops-trend__mark" cx="${x(index).toFixed(1)}" cy="${y(value).toFixed(1)}" r="2.5" fill="${item.color}"><title>${escapeHtml(`${item.label} · ${labels[index]}: ${value}`)}</title></circle>`
    )).join('');
    return `<path class="ops-trend__line" d="${path}" stroke="${item.color}"></path>${marks}`;
  }).join('');

  const legend = shown.map((item) => (
    `<span class="ops-trend__key"><i style="background:${item.color}"></i>${escapeHtml(item.label)}</span>`
  )).join('');

  return `<div class="ops-trend__legend">${legend}</div>
    <svg class="ops-trend__svg" viewBox="0 0 ${CHART.width} ${CHART.height}" role="img" aria-label="${escapeHtml(shown.map((item) => item.label).join(', '))}">
      ${grid}${lines}${xAxis}
    </svg>`;
}

export function trendSectionMarkup({ series = {}, locale = 'en' } = {}) {
  const copy = overviewCopy(locale);
  return `<section class="ops-trend" data-ops-trend aria-label="${escapeHtml(copy.trend)}">
    <header class="ops-trend__head"><h2>${escapeHtml(copy.trend)}</h2></header>
    <div class="ops-trend__canvas">${trendChartMarkup({ series, locale })}</div>
  </section>`;
}
